import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDown, X } from "lucide-react";
import { api } from "@/api";
import { useLang, FLYER_URL, FLYER_VELA_URL } from "@/i18n";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_water-sports-reserve/artifacts/rw8zncno_1000132413.jpg";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { lang, t } = useLang();
  const [wx, setWx] = useState(null);
  const ref = useRef(null);
  const [flyerOpen, setFlyerOpen] = useState(false);
  const [flyerTab, setFlyerTab] = useState("kayak");

  useEffect(() => {
    api.get("/weather").then((r) => setWx(r.data)).catch(() => {});
  }, []);

  const weatherText = wx
    ? `${lang === "it" ? "Vento" : "Wind"} ${lang === "it" ? wx.wind_label_it : wx.wind_label_en} ${Math.round(wx.wind_speed_kn)} kn · ${Math.round(wx.temperature_c)}°C · ${lang === "it" ? wx.condition_it : wx.condition_en}${wx.stale ? " · cache" : ""}`
    : t.hero.weather;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-lake-deep overflow-hidden grain">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 flex">
        <div className="relative w-1/2 h-full overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Kayak sotto la Rocca di Manerba"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="relative w-1/2 h-full overflow-hidden border-l-2 border-gold/40">
          <img
            src="https://images.unsplash.com/photo-1729626739623-7f39861a1f60?fm=jpg&q=80&w=1200&auto=format&fit=crop"
            alt="Barca a vela cabinata sul Lago di Garda"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-lake-deep/70 via-lake-deep/30 to-lake-deep" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute top-24 sm:top-28 right-5 sm:right-10 z-10 hidden sm:flex items-center gap-3 font-mono text-[11px] tracking-[0.18em] text-glacier/80 border hairline-light rounded-full px-4 py-2 bg-lake-deep/40 backdrop-blur-md"
        data-testid="weather-badge"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-teal-light animate-pulse" />
        {weatherText}
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-36 sm:pt-44 pb-28 flex flex-col min-h-screen justify-center"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-serif text-glacier text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-4xl">
          <MaskedLine delay={0.55}>{t.hero.l1}</MaskedLine>
          <MaskedLine delay={0.7}>
            <em className="text-gold not-italic font-medium">{t.hero.l2}</em>
          </MaskedLine>
          <MaskedLine delay={0.85}>{t.hero.l3}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-glacier/75 text-base sm:text-lg leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            data-testid="hero-booking-cta"
            to="/prenota"
            className="group bg-gold text-lake-deep font-semibold px-8 py-4 rounded-full text-sm sm:text-base hover:bg-glacier transition-colors duration-300"
          >
            {t.hero.cta}
          </Link>
          <button
            data-testid="open-flyer-modal-btn"
            onClick={() => setFlyerOpen(true)}
            className="relative border border-glacier/30 text-glacier px-8 py-4 rounded-full text-sm sm:text-base hover:bg-glacier/10 transition-colors duration-300"
          >
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-terracotta animate-ping" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-terracotta" />
            {t.hero.flyer}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="mt-6 font-mono text-[11px] sm:text-xs tracking-[0.15em] text-gold/90"
          data-testid="resident-discount-note"
        >
          −20% · {t.hero.discount}
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-5 sm:left-8 z-10 flex items-center gap-4 text-glacier/60"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="font-mono text-[11px] uppercase tracking-[0.25em]">{t.hero.scroll}</span>
        <span className="hidden sm:inline font-mono text-[11px] tracking-[0.15em] text-glacier/40">
          {t.hero.coords}
        </span>
      </motion.div>

      <motion.a
        data-testid="hero-sailing-card"
        href="#cabinati"
        initial={{ opacity: 0, x: 40, rotate: 4 }}
        animate={{ opacity: 1, x: 0, rotate: 2 }}
        transition={{ delay: 1.7, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-8 xl:right-16 top-[36%] z-10 hidden lg:block group"
      >
        <div className="w-60 border border-glacier/25 rounded-md overflow-hidden bg-lake-deep/50 backdrop-blur-md shadow-2xl group-hover:-rotate-1 group-hover:scale-[1.03] transition-[transform] duration-500">
          <img
            src="https://images.unsplash.com/photo-1698179486922-762cffd0753e?fm=jpg&q=80&w=600&auto=format&fit=crop"
            alt="Barca a vela sul Lago di Garda"
            className="w-full aspect-[4/3] object-cover"
          />
          <div className="px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
              {lang === "it" ? "Esperienza Vela" : "Sailing Experience"}
            </p>
            <p className="text-glacier text-base mt-0.5 font-serif">
              {lang === "it" ? "Scopri i nostri cabinati →" : "Discover our cabin cruisers →"}
            </p>
          </div>
        </div>
      </motion.a>

      <div className="absolute bottom-0 right-6 sm:right-12 z-10 hidden lg:block opacity-70">
        <div className="relative w-40 h-40 animate-spin-slow">
          <svg viewBox="0 0 100 100" className="w-full h-full text-glacier/50">
            <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="50" cy="50" r="36" fill="none" stroke="currentColor" strokeWidth="0.3" strokeDasharray="2 3" />
            <path d="M50 8 L54 50 L50 92 L46 50 Z" fill="#E5A93B" opacity="0.9" />
            <path d="M8 50 L50 46 L92 50 L50 54 Z" fill="currentColor" opacity="0.5" />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {flyerOpen && (
          <motion.div
            data-testid="flyer-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-lake-deep/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setFlyerOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="flyer-modal-close"
                onClick={() => setFlyerOpen(false)}
                className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-gold text-lake-deep flex items-center justify-center hover:bg-glacier transition-colors"
                aria-label="close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex gap-2 justify-center mb-3">
                <button
                  data-testid="flyer-tab-kayak"
                  onClick={() => setFlyerTab("kayak")}
                  className={`font-mono text-[11px] uppercase tracking-[0.15em] border rounded-full px-4 py-2 transition-colors ${
                    flyerTab === "kayak" ? "bg-gold text-lake-deep border-gold" : "border-glacier/30 text-glacier/70 hover:text-glacier"
                  }`}
                >
                  Esperienza Kayak
                </button>
                <button
                  data-testid="flyer-tab-vela"
                  onClick={() => setFlyerTab("vela")}
                  className={`font-mono text-[11px] uppercase tracking-[0.15em] border rounded-full px-4 py-2 transition-colors ${
                    flyerTab === "vela" ? "bg-gold text-lake-deep border-gold" : "border-glacier/30 text-glacier/70 hover:text-glacier"
                  }`}
                >
                  Esperienza Vela
                </button>
              </div>
              <img
                src={flyerTab === "vela" ? FLYER_VELA_URL : FLYER_URL}
                alt="Volantino ufficiale Circolo Vela Manerba del Garda"
                className="max-h-[75vh] w-auto rounded-md shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
